import {
  type ReactNode,
  createContext,
  useContext,
  useMemo,
  useState,
  useLayoutEffect,
  useEffect,
} from "react";
import { useIsIOS } from "@/hooks/useIsIOS";
import { useIsAndroid } from "@/hooks/useIsAndroid";

const MOBILE_BREAKPOINT = 668;

interface MobileContextType {
  isMobile: boolean;
  isIOS: boolean | undefined;
  isAndroid: boolean | undefined;
  isPWA: boolean | undefined;
  isMobileScreen: boolean;
  isTouch: boolean;
}

const MobileContext = createContext<MobileContextType | undefined>(undefined);

export function useMobile() {
  const context = useContext(MobileContext);
  if (context === undefined) {
    throw new Error("useMobile must be used within a MobileProvider");
  }
  return context;
}

export function MobileProvider({ children }: { children: ReactNode }) {
  const [isMobileScreen, setIsMobileScreen] = useState(false);
  const [isTouch, setIsTouch] = useState(false);
  const isIOS = useIsIOS();
  const isAndroid = useIsAndroid();

  const isPWA = isIOS || isAndroid;

  // Safely handle useLayoutEffect during SSR
  const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

  // Detect touch capability
  useEffect(() => {
    const detectTouch = () => {
      return (
        typeof window !== "undefined" &&
        ("ontouchstart" in window ||
          navigator.maxTouchPoints > 0 ||
          (navigator as any).msMaxTouchPoints > 0)
      );
    };

    setIsTouch(detectTouch());
  }, []);

  useIsomorphicLayoutEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const onChange = () => {
      setIsMobileScreen(mql.matches);
    };

    // Set initial value
    setIsMobileScreen(mql.matches);

    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  const value = useMemo(
    () => ({
      isMobile: isMobileScreen,
      isPWA,
      isIOS,
      isAndroid,
      isMobileScreen,
      isTouch,
    }),
    [isMobileScreen, isTouch, isIOS, isAndroid, isPWA],
  );

  return <MobileContext.Provider value={value}>{children}</MobileContext.Provider>;
}
